import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/motion/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_TYPES = [
  "E-Commerce",
  "Campaign / Editorial",
  "Product / Still Life",
  "Motion / Social",
  "Creative Direction",
  "Other",
];

const EMPTY = { name: "", email: "", company: "", projectType: "", message: "" };

export const ContactForm = ({ section = "Stylist", projectTypes = DEFAULT_TYPES }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, section });
      toast.success("Thank you — your inquiry has been sent. I’ll be in touch soon.");
      setForm(EMPTY);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again or email directly.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full border-b border-ink/25 bg-transparent pb-3 pt-1 font-display text-lg font-extralight tracking-tight text-ink outline-none transition-colors duration-300 placeholder:text-muted/80 focus:border-ink md:text-xl";

  return (
    <Reveal>
      <form data-testid="contact-form" onSubmit={submit} className="flex flex-col gap-12">
        <div className="grid gap-12 md:grid-cols-2">
          <label className="block">
            <span className="overline mb-4 block text-muted">Your Name *</span>
            <input data-testid="contact-name" type="text" value={form.name} onChange={update("name")} placeholder="Jane Doe" className={inputCls} />
          </label>
          <label className="block">
            <span className="overline mb-4 block text-muted">Email *</span>
            <input data-testid="contact-email" type="email" value={form.email} onChange={update("email")} placeholder="you@brand.com" className={inputCls} />
          </label>
        </div>

        <div className="grid gap-12 md:grid-cols-2">
          <label className="block">
            <span className="overline mb-4 block text-muted">Company / Brand</span>
            <input data-testid="contact-company" type="text" value={form.company} onChange={update("company")} placeholder="Optional" className={inputCls} />
          </label>
          <label className="block">
            <span className="overline mb-4 block text-muted">Project Type</span>
            <select data-testid="contact-project-type" value={form.projectType} onChange={update("projectType")} className={`${inputCls} cursor-pointer`}>
              <option value="">Select…</option>
              {projectTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="overline mb-4 block text-muted">Message *</span>
          <textarea data-testid="contact-message" value={form.message} onChange={update("message")} rows={3} placeholder="Tell me about the project…" className={`${inputCls} resize-none`} />
        </label>

        <button
          data-testid="contact-submit"
          type="submit"
          disabled={submitting}
          className="group inline-flex w-fit items-center gap-3 border border-ink px-10 py-4 transition-colors duration-500 hover:bg-ink hover:text-paper disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="overline">{submitting ? "Sending…" : "Send Inquiry"}</span>
          <span className="transition-transform duration-500 group-hover:translate-x-1.5">↗</span>
        </button>
      </form>
    </Reveal>
  );
};
